/**
 * 宿主提供的共享模块(D716,experimental):和 React 一样在运行时由宿主解析,不进
 * 小组件源码,一个窗口只加载一份,首次有小组件用到时才加载。编译器白名单、Direct
 * 执行器、交互预览、插件 SDK 构建与出厂模板打包都读这一张表。
 *
 * 版本由宿主固定;以后加第二个共享库走同一张表,每加一个在
 * `docs/architecture/widget-runtime.md`「宿主共享模块」记下理由。
 */

/** 宿主装的 `three` 版本(与 `@xuwo/core` 的依赖同一个精确版本)。 */
export const WIDGET_THREE_VERSION = '0.186.1';

/**
 * 可以 `import … from 'three/addons/<路径>'` 的附加模块。只收这些:每一项在应用
 * 构建里是一个按需加载的分块,交互预览把它们打在同一个运行时文件里。缺的按真实
 * 需要加。
 */
export const WIDGET_THREE_ADDONS = [
  'controls/OrbitControls.js',
  'environments/RoomEnvironment.js',
  'geometries/RoundedBoxGeometry.js',
  'loaders/GLTFLoader.js',
  'math/SimplexNoise.js',
  'objects/Sky.js',
  'postprocessing/BokehPass.js',
  'postprocessing/EffectComposer.js',
  'postprocessing/GTAOPass.js',
  'postprocessing/OutputPass.js',
  'postprocessing/Pass.js',
  'postprocessing/RenderPass.js',
  'postprocessing/SMAAPass.js',
  'postprocessing/ShaderPass.js',
  'postprocessing/UnrealBloomPass.js',
  'shaders/CopyShader.js',
  'shaders/FXAAShader.js',
  'shaders/HorizontalTiltShiftShader.js',
  'shaders/VerticalTiltShiftShader.js',
  'utils/BufferGeometryUtils.js',
] as const;

export type WidgetSharedModule =
  | 'three'
  | `three/addons/${(typeof WIDGET_THREE_ADDONS)[number]}`;

export const WIDGET_SHARED_MODULES: readonly WidgetSharedModule[] = [
  'three',
  ...WIDGET_THREE_ADDONS.map(path => `three/addons/${path}` as const),
];

const shared = new Set<string>(WIDGET_SHARED_MODULES);

/**
 * 宿主一直提供的基础模块:React 与作者接口。与上面的共享库一起构成小组件能导入的
 * 全部外部模块(`WidgetHostModule`):编译器白名单、插件 SDK 构建的外部化与插件组件的
 * 模块加载器都读这两张表。
 */
export const WIDGET_HOST_BASE_MODULES = [
  'react',
  'react/jsx-runtime',
  'react-dom',
  'react-dom/client',
  '@xuwo/widget',
  '@xuwo/widget/ui',
] as const;

export type WidgetHostModule =
  | (typeof WIDGET_HOST_BASE_MODULES)[number]
  | WidgetSharedModule;

const base = new Set<string>(WIDGET_HOST_BASE_MODULES);

export function isWidgetHostModule(
  specifier: string
): specifier is WidgetHostModule {
  return base.has(specifier) || shared.has(specifier);
}

/**
 * 插件组件模块(D717 A)从这个全局登记表拿宿主模块:`globalThis[Symbol.for(键)]`
 * 上的 `load(name)`(异步,首次用到时加载共享库)与 `require(name)`(同步,取已加载的
 * 同一份实例)。`xuwo-plugin build` 把对宿主模块的导入改写成这两个调用,渲染端的
 * 包组件加载器装好这张表;键是两边的唯一约定。
 */
export const WIDGET_HOST_MODULES_GLOBAL = 'xuwo.widget.host-modules';

export function isWidgetSharedModule(
  specifier: string
): specifier is WidgetSharedModule {
  return shared.has(specifier);
}

/**
 * 宿主编译器产出的 CommonJS 代码(文档、组件库与内联组件)里 `require` 到的共享模块。
 * 执行器据此只加载用到的那几个;不在表里的名字留给 `require` 垫片按
 * `WIDGET_IMPORT_UNAVAILABLE` 拒绝。
 */
export function widgetSharedModulesIn(
  code: string
): readonly WidgetSharedModule[] {
  if (!code.includes('three')) return [];
  const found = new Set<WidgetSharedModule>();
  for (const match of code.matchAll(
    /\brequire\(\s*(['"])(three(?:\/[^'"\s]*)?)\1\s*\)/gu
  ))
    if (isWidgetSharedModule(match[2])) found.add(match[2]);
  return [...found];
}
